import React, { useState, useCallback } from 'react';
import { List, useListContext, useNotify, useRefresh, useGetOne } from 'react-admin';
import Papa from 'papaparse';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import CircularProgress from '@mui/material/CircularProgress';

import { useTitle } from '@src/hooks/useTitle';
import type { PartnerSettings } from '@src/settings';

import PricingRulesToolbar from './PricingRulesToolbar';
import PricingRulesDataTable from './PricingRulesDataTable';
import PricingRulesActions from './PricingRulesActions';
import { pricingRulesDataProvider } from './dataProvider';

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

/**
 * Status banner showing apply status and Apply button.
 * Shows when pricing rules have been modified but not yet applied.
 */
const PricingStatusBanner = () => {
  const { meta } = useListContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [isApplying, setIsApplying] = useState(false);

  const initiatedAt = meta?.initiatedAt as string | null | undefined;
  const processedAt = meta?.processedAt as string | null | undefined;

  const handleApply = useCallback(async () => {
    setIsApplying(true);
    try {
      await pricingRulesDataProvider.initiate('pricingRules', {});
      notify('Pricing rules are being applied', { type: 'success' });
      refresh();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to apply pricing rules';
      notify(message, { type: 'error' });
    } finally {
      setIsApplying(false);
    }
  }, [notify, refresh]);

  // Determine status
  const isProcessing = initiatedAt && !processedAt;
  const isProcessed = initiatedAt && processedAt;
  // Show unapplied state when not processing and not processed (i.e., initiatedAt is null)
  const showUnappliedState = !initiatedAt;

  return (
    <Paper sx={{ p: 2, mt: 2, mb: 2, mx: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          {showUnappliedState && (
            <Alert severity="warning" sx={{ py: 0 }}>
              <Typography variant="body2">
                <strong>Unapplied changes pending.</strong>
                {' '}
                Click Apply to process pricing rules.
              </Typography>
            </Alert>
          )}
          {isProcessing && (
            <Alert severity="info" sx={{ py: 0 }}>
              <Typography variant="body2">
                <strong>INITIATED:</strong>
                {' '}
                {formatDate(initiatedAt)}
                {' '}
                — Processing in progress...
              </Typography>
            </Alert>
          )}
          {isProcessed && (
            <Alert severity="success" sx={{ py: 0 }}>
              <Typography variant="body2">
                <strong>Processed:</strong>
                {' '}
                {formatDate(processedAt)}
              </Typography>
            </Alert>
          )}
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={handleApply}
          disabled={isApplying || !!initiatedAt}
          startIcon={isApplying ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isApplying ? 'Applying...' : 'Apply'}
        </Button>
      </Box>
    </Paper>
  );
};

const SAMPLE_TSV_COLUMNS = [
  'source_column',
  'department',
  'category',
  'merchant_category',
  'upc',
  'primary_tag',
  'primary_name',
  'secondary_tag',
  'secondary_name',
  'tertiary_tag',
  'tertiary_name',
  'discount',
] as const;

const SAMPLE_TSV_ROWS = [
  { source_column: 'price', discount: 80 },
  { source_column: 'price', department: "Men's", discount: 75 },
  { source_column: 'price', department: "Women's", category: 'Shoes', discount: 70 },
  { source_column: 'msrp_price', merchant_category: 'Outerwear', discount: 85 },
  { source_column: 'absolute_price', primary_tag: 'brand', primary_name: 'Acme', discount: 1999 },
  { source_column: 'price', primary_tag: 'brand', primary_name: 'Generic', secondary_tag: 'color', secondary_name: 'Blue', tertiary_tag: 'size', tertiary_name: 'Large', discount: 65 },
];

const handleDownloadSample = () => {
  const tsv = Papa.unparse(SAMPLE_TSV_ROWS, {
    delimiter: '\t',
    columns: SAMPLE_TSV_COLUMNS as unknown as string[],
  });
  const blob = new Blob([tsv], { type: 'text/tab-separated-values' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'pricing_rules_sample.tsv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const PricingRulesDocumentation = () => (
  <Paper sx={{ p: 2, mt: 2, backgroundColor: 'grey.50' }}>
    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
      Guide
    </Typography>

    <Typography variant="body2" paragraph>
      Pricing rules determine how resale prices are calculated for items in your catalog.
      Each rule specifies criteria (like department, category, or brand) and either a discount
      percentage or a fixed price. When an item matches a rule, Trove uses that rule to calculate
      its resale price.
    </Typography>

    <Typography variant="body2" fontWeight="bold" sx={{ mt: 2 }}>
      How rules are matched
    </Typography>
    <Typography variant="body2" paragraph>
      Rules are evaluated in position order (0 first). The first rule that matches an item is used.
      More specific rules should be placed before general rules. For example, a rule for
      &quot;Kids&apos; shoes&quot; should come before a general &quot;all shoes&quot; rule.
      We recommend having a catch-all rule at the end (no filters) to ensure every item gets a price.
    </Typography>

    <Typography variant="body2" fontWeight="bold" sx={{ mt: 2 }}>
      Why you need to Apply changes
    </Typography>
    <Typography variant="body2" paragraph>
      Pricing rules are applied to your entire catalog, which can contain thousands of items.
      Instead of recalculating prices after every edit, Trove lets you make multiple changes
      and then apply them all at once. This is faster and lets you review your rules before
      they take effect.
    </Typography>

    <Typography variant="body2" fontWeight="bold" sx={{ mt: 2 }}>
      Status states
    </Typography>
    <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 2 }}>
      <li>
        <Typography variant="body2">
          <em>Unapplied</em>
          {' '}
          — You have made changes that are not yet reflected in your catalog prices.
          Click Apply when you are ready.
        </Typography>
      </li>
      <li>
        <Typography variant="body2">
          <em>Processing</em>
          {' '}
          — Trove is calculating prices for all items in your catalog. This may take a few minutes
          for large catalogs. You cannot make changes while processing is in progress.
        </Typography>
      </li>
      <li>
        <Typography variant="body2">
          <em>Processed</em>
          {' '}
          — All catalog items have been priced according to your rules.
          You can now make additional changes and apply them when ready.
        </Typography>
      </li>
    </Box>

    <Typography variant="body2" fontWeight="bold" sx={{ mt: 2 }}>
      Discount vs. fixed price
    </Typography>
    <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 2 }}>
      <li>
        <Typography variant="body2">
          Most source columns (price, msrp_price, etc.) use a
          {' '}
          <strong>discount percentage</strong>
          . A value of 80 means 80% of the source price.
        </Typography>
      </li>
      <li>
        <Typography variant="body2">
          The
          {' '}
          <strong>absolute_price</strong>
          {' '}
          source column sets a fixed price in cents (e.g., 1999 = $19.99).
        </Typography>
      </li>
    </Box>

    <Typography variant="body2" fontWeight="bold" sx={{ mt: 2 }}>
      Importing rules
    </Typography>
    <Typography variant="body2">
      You can bulk-import rules from a TSV file. Note that importing replaces all existing rules.
      {' '}
      <Box
        component="span"
        onClick={handleDownloadSample}
        sx={{
          color: 'primary.main',
          textDecoration: 'underline',
          cursor: 'pointer',
          '&:hover': { textDecoration: 'none' },
        }}
      >
        Download a sample file
      </Box>
      {' '}
      to see the expected format.
    </Typography>
  </Paper>
);

const PricingRulesList = () => {
  const { title } = useTitle('Pricing Rules');
  const { data: settings, isLoading } = useGetOne<PartnerSettings & { id: number }>('settings', { id: 0 });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (settings?.useAbsoluteValuesForPricing) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <AlertTitle>Pricing Rules Unavailable</AlertTitle>
          <Typography variant="body2" paragraph>
            This page is not available because your partner account has
            {' '}
            <strong>Use Absolute Values for Pricing</strong>
            {' '}
            enabled. This setting uses a different pricing model that is incompatible with the
            Pricing Rules interface.
          </Typography>
          <Typography variant="body2">
            To use this page, either:
          </Typography>
          <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
            <li>
              <Typography variant="body2">
                Contact your Trove representative to disable the
                {' '}
                <em>Use Absolute Values for Pricing</em>
                {' '}
                setting, or
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Use the Trove Admin portal to manage pricing rules instead.
              </Typography>
            </li>
          </Box>
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <List
        perPage={10}
        sort={{ field: 'position', order: 'ASC' }}
        title={title}
        actions={<PricingRulesActions />}
      >
        <PricingStatusBanner />
        <PricingRulesToolbar />
        <PricingRulesDataTable />
      </List>
      <Box sx={{ px: 2, pb: 2 }}>
        <PricingRulesDocumentation />
      </Box>
    </>
  );
};

export default PricingRulesList;
